import { useState, useEffect, useRef } from "react";
import { getVisionAssistQuestions, getVisionAssistRecords } from "../../actions/aiAction";
import "./VisionAssistPanel.css";

const storageKey = (planId) => `dmyor_vap_${planId}`;

const loadSaved = (planId) => {
  if (!planId) return null;
  try {
    const raw = localStorage.getItem(storageKey(planId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const VisionAssistPanel = ({ plan, onAdd, onClose }) => {
  const saved = loadSaved(plan?._id);

  // Reload-resistant state. Anything user typed survives close/reopen and refresh.
  const initialPhase = saved?.phase || "direction";

  const [phase, setPhase] = useState(initialPhase);
  const [direction, setDirection] = useState(saved?.direction ?? "");
  const [opening, setOpening] = useState(saved?.opening ?? "");
  const [questions, setQuestions] = useState(saved?.questions ?? []);
  const [answers, setAnswers] = useState(saved?.answers ?? {});
  const [suggestions, setSuggestions] = useState(saved?.suggestions ?? []);
  const [selected, setSelected] = useState(
    new Set(Array.isArray(saved?.selected) ? saved.selected : [])
  );
  const [errorMsg, setErrorMsg] = useState(saved?.errorMsg ?? "");
  const [conversationHistory, setConversationHistory] = useState(saved?.conversationHistory ?? []);
  const [otherTexts, setOtherTexts] = useState(saved?.otherTexts ?? {});
  const [refinement, setRefinement] = useState(saved?.refinement ?? "");

  // Track the last stable (non-loading) phase. If user closes mid-fetch, we
  // restore them to where they were before the request fired, not to a phase
  // that is waiting on a request that no longer exists.
  const lastStablePhase = useRef(initialPhase !== "loading" ? initialPhase : "direction");

  useEffect(() => {
    if (phase !== "loading") lastStablePhase.current = phase;
  }, [phase]);

  useEffect(() => {
    if (!plan?._id) return;
    try {
      const payload = {
        // Never persist "loading" — restore to the previous stable phase instead.
        phase: phase === "loading" ? lastStablePhase.current : phase,
        direction,
        opening,
        questions,
        answers,
        suggestions,
        selected: Array.from(selected),
        errorMsg,
        conversationHistory,
        otherTexts,
        refinement,
      };
      localStorage.setItem(storageKey(plan._id), JSON.stringify(payload));
    } catch (_) { /* quota — silent */ }
  }, [plan?._id, phase, direction, opening, questions, answers, suggestions, selected, errorMsg, conversationHistory, otherTexts, refinement]);

  const clearSaved = () => {
    if (!plan?._id) return;
    try { localStorage.removeItem(storageKey(plan._id)); } catch (_) {}
  };

  const restart = () => {
    clearSaved();
    setPhase("direction");
    setDirection("");
    setOpening("");
    setQuestions([]);
    setAnswers({});
    setSuggestions([]);
    setSelected(new Set());
    setErrorMsg("");
    setConversationHistory([]);
    setOtherTexts({});
    setRefinement("");
  };

  const refineSuggestions = async () => {
    const extra = refinement.trim();
    if (!extra) return;
    const allPrevQA = conversationHistory.flatMap(r => r.questionsAndAnswers);
    const extended = [
      ...allPrevQA,
      { question: "Additional context / refinement from user", answer: extra },
    ];
    setRefinement("");
    await fetchSuggestions(extended);
  };

  // answers[q.id] = string[] for mcq, string for text
  const toggleMcqOption = (qId, opt) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[qId]) ? prev[qId] : [];
      const exists = current.includes(opt);
      return { ...prev, [qId]: exists ? current.filter(o => o !== opt) : [...current, opt] };
    });
  };

  const serializeAnswers = (qs, ans, others = otherTexts) =>
    qs.map(q => ({
      question: q.question,
      answer: q.type === "mcq"
        ? (Array.isArray(ans[q.id])
            ? ans[q.id]
                .map(o => (o === "__other__" ? (others[q.id] || "").trim() : o))
                .filter(Boolean)
                .join(", ")
            : "")
        : (ans[q.id] || ""),
    }));

  const allAnswered = questions.every(q => {
    if (q.type === "mcq") {
      const arr = Array.isArray(answers[q.id]) ? answers[q.id] : [];
      if (arr.length === 0) return false;
      if (arr.includes("__other__") && !(otherTexts[q.id] || "").trim()) return false;
      return true;
    }
    return (answers[q.id] || "").trim().length > 0;
  });

  const fetchQuestions = async (history = [], answersForEval = null, dir = "") => {
    setPhase("loading");
    try {
      const data = await getVisionAssistQuestions(plan._id, history, answersForEval, dir);
      setOpening(data.opening || "");
      if (data.readyToSuggest) {
        const allQA = [...history.flatMap(r => r.questionsAndAnswers), ...(answersForEval || [])];
        await fetchSuggestions(allQA);
      } else {
        setQuestions(data.questions || []);
        setAnswers({});
        setOtherTexts({});
        setPhase("questions");
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Something went wrong. Try again.");
      setPhase("error");
    }
  };

  const fetchSuggestions = async (allQA) => {
    setPhase("loading");
    try {
      const data = await getVisionAssistRecords(plan._id, allQA);
      setSuggestions(data.records || []);
      setSelected(new Set((data.records || []).map((_, i) => i)));
      setPhase("suggestions");
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Failed to generate suggestions.");
      setPhase("error");
    }
  };

  const handleSubmitAnswers = async () => {
    const qa = serializeAnswers(questions, answers);
    const history = [...conversationHistory];
    setPhase("loading");
    try {
      const data = await getVisionAssistQuestions(plan._id, history, qa, direction);
      setOpening(data.opening || "");
      if (data.readyToSuggest) {
        const allPrevQA = history.flatMap(r => r.questionsAndAnswers);
        await fetchSuggestions([...allPrevQA, ...qa]);
      } else {
        setConversationHistory(prev => [...prev, { questionsAndAnswers: qa }]);
        setQuestions(data.questions || []);
        setAnswers({});
        setOtherTexts({});
        setPhase("questions");
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Something went wrong. Try again.");
      setPhase("error");
    }
  };

  const toggleSelect = (i) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const handleAdd = () => {
    const picked = suggestions
      .filter((_, i) => selected.has(i))
      .map((s) => (typeof s === "string" ? s : s.record));
    if (picked.length === 0) return;
    clearSaved();
    onAdd(picked);
  };

  return (
    <div className="vap-overlay" onClick={onClose}>
      <div className="vap-panel" onClick={e => e.stopPropagation()}>

        <div className="vap-header">
          <div className="vap-header-left">
            <span className="vap-eyebrow">AI Assist</span>
            <span className="vap-title">Figure out your records</span>
          </div>
          <div className="vap-header-actions">
            {phase !== "direction" && phase !== "loading" && (
              <button
                type="button"
                className="vap-restart"
                onClick={restart}
                title="Restart the flow"
              >
                ↻ Restart
              </button>
            )}
            <button className="vap-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        <div className="vap-body">

          {phase === "direction" && (
            <>
              <p className="vap-opening" style={{ textAlign: "left" }}>Tell us about this vision. What are you trying to build, become, or make real? The more specific you are, the better the records we suggest.</p>
              <textarea
                className="vap-text-answer"
                placeholder="e.g. I'm building a SaaS for freelancers. Currently pre-revenue, have a working prototype, looking to get first 10 paying users..."
                value={direction}
                onChange={e => setDirection(e.target.value)}
                rows={4}
                autoFocus
              />
              <div className="vap-footer">
                <button className="vap-submit" onClick={() => fetchQuestions([], null, direction)}>
                  {direction.trim() ? "Generate records" : "Skip, just ask me questions"}
                </button>
              </div>
            </>
          )}

          {phase === "loading" && (
            <div className="vap-loading">
              <div className="vap-spinner" />
              <p>Thinking...</p>
            </div>
          )}

          {phase === "error" && (
            <div className="vap-error">
              <p>{errorMsg}</p>
              <button className="vap-retry" onClick={() => fetchQuestions()}>Try again</button>
            </div>
          )}

          {phase === "questions" && (
            <>
              {opening && <p className="vap-opening">{opening}</p>}
              <div className="vap-questions">
                {questions.map(q => (
                  <div key={q.id} className="vap-question">
                    <p className="vap-q-text">{q.question}</p>
                    {q.type === "mcq" && q.options ? (
                      <>
                        <p className="vap-q-hint">Select all that apply</p>
                        <div className="vap-options">
                          {q.options.map(opt => {
                            const isSelected = Array.isArray(answers[q.id]) && answers[q.id].includes(opt);
                            return (
                              <button
                                key={opt}
                                type="button"
                                className={`vap-option${isSelected ? " selected" : ""}`}
                                onClick={() => toggleMcqOption(q.id, opt)}
                              >
                                {isSelected && <span className="vap-option-check">✓</span>}
                                {opt}
                              </button>
                            );
                          })}
                          {(() => {
                            const otherSelected = Array.isArray(answers[q.id]) && answers[q.id].includes("__other__");
                            return (
                              <button
                                type="button"
                                className={`vap-option${otherSelected ? " selected" : ""}`}
                                onClick={() => toggleMcqOption(q.id, "__other__")}
                              >
                                {otherSelected && <span className="vap-option-check">✓</span>}
                                Other
                              </button>
                            );
                          })()}
                        </div>
                        {Array.isArray(answers[q.id]) && answers[q.id].includes("__other__") && (
                          <input
                            type="text"
                            className="vap-other-input"
                            placeholder="Type your answer…"
                            value={otherTexts[q.id] || ""}
                            onChange={e => setOtherTexts(prev => ({ ...prev, [q.id]: e.target.value }))}
                          />
                        )}
                      </>
                    ) : (
                      <textarea
                        className="vap-text-answer"
                        placeholder="Your answer..."
                        value={answers[q.id] || ""}
                        onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                        rows={3}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="vap-footer">
                <button className="vap-submit" onClick={handleSubmitAnswers} disabled={!allAnswered}>
                  Continue
                </button>
              </div>
            </>
          )}

          {phase === "suggestions" && (
            <>
              {opening && <p className="vap-opening">{opening}</p>}
              <p className="vap-suggestions-hint">Select records to add. You can edit them after.</p>
              <div className="vap-suggestions">
                {suggestions.map((s, i) => {
                  const text = typeof s === "string" ? s : s.record;
                  const reasoning = typeof s === "string" ? "" : s.reasoning;
                  const isSelected = selected.has(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`vap-suggestion${isSelected ? " selected" : ""}`}
                      onClick={() => toggleSelect(i)}
                    >
                      <span className="vap-suggestion-check">{isSelected ? "✓" : "+"}</span>
                      <span className="vap-suggestion-body">
                        <span className="vap-suggestion-text">{text}</span>
                        {reasoning && (
                          <span className="vap-suggestion-reasoning">{reasoning}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="vap-refine">
                <p className="vap-refine-label">Not quite right? Add more context and regenerate.</p>
                <textarea
                  className="vap-text-answer"
                  placeholder="e.g. focus more on daily habits, drop the marketing angle…"
                  value={refinement}
                  onChange={e => setRefinement(e.target.value)}
                  rows={2}
                />
                <button
                  type="button"
                  className="vap-refine-btn"
                  onClick={refineSuggestions}
                  disabled={!refinement.trim()}
                >
                  ✦ Refine suggestions
                </button>
              </div>

              <div className="vap-footer">
                <button className="vap-submit" onClick={handleAdd} disabled={selected.size === 0}>
                  Add {selected.size} record{selected.size === 1 ? "" : "s"}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default VisionAssistPanel;
